import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import SwipeToDelete from '@/components/shared/SwipeToDelete';
import { MarkdownInlineRenderer } from '@/components/ui/MarkdownRenderer';
import { ROUTES } from '@/configs/routes';
import { InsightEntity } from '@/entities/Insights';
import { useSidebarNoteStore } from '@/stores/local/sidebar/SidebarNoteStore';
import { useDevices } from '@/utils/devices';
import { getExpirationStatus } from '@/utils/getExpirationStatus';

import BaseItem from '../../common/BaseItem';
import { InsightsScopeTabKey } from '../InsightsTabContent/types';

type InsightItemProps = {
  className?: string;
  model: InsightEntity;
  actionSlot?: React.ReactNode;
};

const InsightItem: React.FC<InsightItemProps> = ({ className, model, actionSlot }) => {
  const { isPhone } = useDevices();
  const navigate = useNavigate();
  const { id, insightId } = useParams();
  const isActive = insightId === String(model.id);
  const note = getExpirationStatus(model.expirationDate);
  const { insights } = useSidebarNoteStore();
  const canEdit = insights.scopeTabs.activeKey === InsightsScopeTabKey.my;

  const handleClick = React.useCallback(() => {
    navigate(ROUTES.organization.notebook.insights.insight.create(id as string, String(model.id)), {
      state: {
        detailUpdatedAt: model.updatedAt,
        detailCreatedAt: model.createdAt,
        detailTitle: model.title,
      },
    });
  }, [id, model.createdAt, model.id, model.title, model.updatedAt, navigate]);

  const authorNameText = React.useMemo(() => {
    const givenName = model.authorGivenName?.trim();
    const familyName = model.authorFamilyName?.trim();

    if (givenName && familyName) {
      return `${givenName} ${familyName[0].toUpperCase()}.`;
    }

    if (givenName) {
      return givenName;
    }

    return `Пользователь №${model.userId}`;
  }, [model.authorFamilyName, model.authorGivenName, model.userId]);

  const noteText =
    insights.scopeTabs.activeKey === InsightsScopeTabKey.public
      ? [authorNameText, note].filter(Boolean).join(' • ')
      : note;

  const previewText = React.useMemo(() => {
    const normalized = model.text?.trim();

    if (!normalized) {
      return;
    }

    return <MarkdownInlineRenderer content={normalized} />;
  }, [model.text]);

  const handleDelete = React.useCallback(() => {
    if (!canEdit) {
      return;
    }

    insights.remove(String(model.id));
  }, [canEdit, insights, model.id]);

  return (
    <SwipeToDelete
      enabled={isPhone && canEdit}
      isLoading={insights.popupMeta.isLoading}
      isSuccess={insights.popupMeta.isLoaded}
      onDelete={handleDelete}
    >
      <BaseItem
        className={className}
        isActive={isActive}
        title={model.title}
        text={previewText}
        note={noteText}
        actionSlot={actionSlot}
        onClick={handleClick}
      />
    </SwipeToDelete>
  );
};

export default observer(InsightItem);
